import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Card,
  CardContent,
  IconButton,
  useTheme
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { getCategoryIcon } from '../utils/categoryIcons';
import { useNavigate } from 'react-router-dom';
import { useProgramCategories, ProgramCategory } from '../contexts/ProgramCategoriesContext';
import { usePrograms } from '../contexts/ProgramsContext';
import { useSchools } from '../contexts/SchoolsContext';


// Utility function to truncate text to 50 words
const truncateToWords = (text: string, maxWords: number = 50): string => {
  if (!text) return '';
  const words = text.trim().split(/\s+/);
  if (words.length <= maxWords) return text;
  return words.slice(0, maxWords).join(' ') + '...';
};

const ProgramCategories: React.FC = () => {
  const { categories, loading: categoriesLoading, error: categoriesError } = useProgramCategories();
  const { programs, loading: programsLoading } = usePrograms();
  const { schools, loading: schoolsLoading } = useSchools();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Initialize viewMode with localStorage preference, defaulting to 'card'
  const [viewMode, setViewMode] = useState<'card' | 'list'>(() => {
    const savedViewMode = localStorage.getItem('programCategoriesViewMode');
    return (savedViewMode === 'card' || savedViewMode === 'list') ? savedViewMode : 'card';
  });

  // Calculate program counts from context data
  const programCounts = useMemo(() => {
    const counts: { [category: string]: number } = {};
    
    // Create a set of active school names (schools that haven't been deleted)
    const activeSchoolNames = new Set<string>();
    schools.forEach(school => {
      if (!school.SchoolLastDeleted) {
        activeSchoolNames.add(school.name);
      }
    });
    
    // Count programs by category
    programs.forEach(program => {
      // Only count programs that haven't been deleted and whose school still exists
      if (!program.ProgramLastDeleted && activeSchoolNames.has(program.schoolName)) {
        const cat = program.category || '';
        if (cat) {
          counts[cat] = (counts[cat] || 0) + 1;
        }
      }
    });
    
    return counts;
  }, [programs, schools]);

  // Combined loading state
  const loading = categoriesLoading || programsLoading || schoolsLoading;
  
  // Combined error state
  const error = categoriesError || null;

  // Sort categories alphabetically and filter out categories with 0 programs
  const sortedCategories = useMemo(() => {
    return [...categories]
      .filter(cat => (programCounts[cat.id] || 0) > 0) // Only show categories with programs
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, programCounts]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Results Count and View Toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">
            {sortedCategories.length} program categories displayed
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
          <IconButton
            aria-label="Card view"
            color={viewMode === 'card' ? 'primary' : 'default'}
            onClick={() => {
              setViewMode('card');
              localStorage.setItem('programCategoriesViewMode', 'card');
            }}
            size="large"
          >
            <ViewModuleIcon />
          </IconButton>
          <IconButton
            aria-label="List view"
            color={viewMode === 'list' ? 'primary' : 'default'}
            onClick={() => {
              setViewMode('list');
              localStorage.setItem('programCategoriesViewMode', 'list');
            }}
            size="large"
          >
            <ViewListIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Categories Display */}
      {viewMode === 'card' ? (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)'
          },
          gap: 2,
          mb: 3
        }}>
          {sortedCategories.map((cat) => (
            <Card 
              key={cat.id}
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[4],
                  cursor: 'pointer',
                },
                          }}
            onClick={() => navigate(`/programs?category=${encodeURIComponent(cat.id)}`)}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', mb: 2, justifyContent: 'flex-start' }}>
                  <Box
                    sx={{ 
                      width: 45, 
                      height: 45, 
                      bgcolor: '#8dd439', 
                      color: '#000000', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1, 
                      flexShrink: 0,
                      border: '1px solid #000000'
                    }}
                  >
                    {React.createElement(getCategoryIcon(cat.name), { sx: { fontSize: 24 } })}
                  </Box>
                  <Typography variant="h6" component="div" sx={{ flex: 1, wordWrap: 'break-word', lineHeight: 1.2 }}>
                    {cat.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {(programCounts[cat.id] || 0).toLocaleString()} programs
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {truncateToWords(cat.description)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56, fontWeight: 'bold', verticalAlign: 'bottom' }}></TableCell>
                <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom', width: 280 }}>Program Category</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}># of Programs</TableCell>
                <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'bottom' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCategories.map((cat) => (
                <TableRow
                  key={cat.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/programs?category=${encodeURIComponent(cat.id)}`)}
                >
                  <TableCell sx={{ textAlign: 'center', width: 56, verticalAlign: 'top' }}>
                    <Box
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        bgcolor: '#8dd439', 
                        color: '#000000', 
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        border: '1px solid #000000'
                      }}
                    >
                      {React.createElement(getCategoryIcon(cat.name), { sx: { fontSize: 20 } })}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: 280, verticalAlign: 'top' }}>{cat.name}</TableCell>
                  <TableCell align="right" sx={{ verticalAlign: 'top' }}>{(programCounts[cat.id] || 0).toLocaleString()}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>{cat.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ProgramCategories; 